import "./Packages.scss";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useState, useEffect } from "react";
import BlueButton from "../../components/BlueButton/BlueButton";
import AddGif from "./../../assets/add-gif.gif";
import SearchIcon from "@mui/icons-material/Search";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import { DataGrid } from "@mui/x-data-grid";
import { ListsColumns, PackagesColumns } from "../../SourceData";
import { Link, useNavigate } from "react-router-dom";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import api from "../../utils/api";
import { toast } from "react-toastify";
import MessageModals from "../../components/MessageModals/MessageModals";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import { usePermissions } from "./../../hooks/usePermissions";

const Packages = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const navigate = useNavigate();

  // State variables
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [packageType, setPackageType] = useState("");
  const [view, setView] = useState("package");

  // Delete modal state
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Data state
  const [packages, setPackages] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state for lists
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentModalView, setCurrentModalView] = useState("createList");
  const [editingList, setEditingList] = useState(null);

  // Permission helpers
  const getPermissions = () => {
    if (view === "package") {
      return {
        add: 51, // اضافة باقة
        view: 52, // رؤية
        activate: 53, // تفعيل
        edit: 54, // تعديل
        delete: 55, // حذف
      };
    } else {
      return {
        add: 57, // اضافة (قوائم)
        view: 58, // رؤية (قوائم)
        activate: 59, // تفعيل (قوائم)
        edit: 60, // تعديل (قوائم)
        delete: 61, // حذف (قوائم)
      };
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      setPage(1); // Reset to first page when filters change
    }
  };

  // Handle package type change
  const handleTypeChange = (event) => {
    setPackageType(event.target.value);
    setPage(1); // Reset to first page when filters change
  };

  // Handle view change
  const handleViewChange = (newView) => {
    const permissions = newView === "package" ? { view: 52 } : { view: 58 };

    if (!hasPermission(permissions.view)) {
      toast.error(
        `ليس لديك صلاحية لعرض ${newView === "package" ? "الباقات" : "القوائم"}`
      );
      return;
    }

    setView(newView);
    setPage(1);
  };

  // Fetch packages data
  const fetchPackages = async () => {
    const permissions = getPermissions();
    // Check view permission before fetching data
    if (!hasPermission(permissions.view)) {
      return;
    }

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.name = searchTerm;
        params.local = "ar";
      }

      // Add type filter if not empty
      if (packageType) {
        params.type = packageType;
      }

      const response = await api.get("/packages", { params });

      if (response.data.status === "success") {
        // Ensure we always set an array, even if data is null/undefined
        setPackages(
          Array.isArray(response.data.data) ? response.data.data : []
        );
        setTotalCount(response.data.totalCount || 0);
      } else {
        // Handle unsuccessful response
        setPackages([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("حدث خطأ أثناء تحميل الباقات");
      // Set empty array on error to prevent map errors
      setPackages([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch lists data
  const fetchLists = async () => {
    const permissions = getPermissions();
    // Check view permission before fetching data
    if (!hasPermission(permissions.view)) {
      return;
    }

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.name = searchTerm;
        params.local = "ar";
      }

      const response = await api.get("/package-lists", { params });

      if (response.data.status === "success") {
        // Ensure we always set an array, even if data is null/undefined
        setLists(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalCount(response.data.totalDocuments || 0);
      } else {
        // Handle unsuccessful response
        setLists([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching lists:", error);
      toast.error("حدث خطأ أثناء تحميل القوائم");
      // Set empty array on error to prevent map errors
      setLists([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch data when dependencies change
  useEffect(() => {
    if (view === "package") {
      fetchPackages();
    } else {
      fetchLists();
    }
  }, [page, pageSize, searchTerm, packageType, view]);

  // Toggle package active state
  const handlePackageActive = async (id) => {
    const permissions = getPermissions();
    // Check activation permission
    if (!hasPermission(permissions.activate)) {
      toast.error(
        `ليس لديك صلاحية لتفعيل/إلغاء تفعيل ${
          view === "package" ? "الباقات" : "القوائم"
        }`
      );
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/packages/${id}/toggleState`);
      toast.success("تم تغيير حالة الباقة بنجاح");
      fetchPackages();
    } catch (error) {
      console.error("Error toggling package status:", error);
      toast.error("حدث خطأ أثناء تغيير حالة الباقة");
    } finally {
      setLoading(false);
    }
  };

  // Toggle list active state
  const handleListActive = async (id) => {
    const permissions = getPermissions();
    // Check activation permission
    if (!hasPermission(permissions.activate)) {
      toast.error(
        `ليس لديك صلاحية لتفعيل/إلغاء تفعيل ${
          view === "package" ? "الباقات" : "القوائم"
        }`
      );
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/package-lists/${id}/toggle`);
      toast.success("تم تغيير حالة القائمة بنجاح");
      fetchLists();
    } catch (error) {
      console.error("Error toggling list status:", error);
      toast.error("حدث خطأ أثناء تغيير حالة القائمة");
    } finally {
      setLoading(false);
    }
  };

  // Handle create button click
  const handleCreate = () => {
    const permissions = getPermissions();
    // Check add permission
    if (!hasPermission(permissions.add)) {
      toast.error(
        `ليس لديك صلاحية لإضافة ${view === "package" ? "الباقات" : "القوائم"}`
      );
      return;
    }

    if (view === "package") {
      navigate("/packages/create");
    } else {
      setCurrentModalView("createList");
      setEditingList(null);
      setIsModalOpen(true);
    }
  };

  // Handle edit package click
  const handleEditPackage = (packageId) => {
    const permissions = getPermissions();
    // Check edit permission
    if (!hasPermission(permissions.edit)) {
      toast.error(
        `ليس لديك صلاحية لتعديل ${view === "package" ? "الباقات" : "القوائم"}`
      );
      return;
    }
    // Navigation will happen through Link component
  };

  // Handle edit list
  const handleEditList = (listItem) => {
    const permissions = getPermissions();
    // Check edit permission
    if (!hasPermission(permissions.edit)) {
      toast.error(
        `ليس لديك صلاحية لتعديل ${view === "package" ? "الباقات" : "القوائم"}`
      );
      return;
    }

    setCurrentModalView("editList");
    setEditingList(listItem);
    setIsModalOpen(true);
  };

  // Handle delete click
  const handleDeleteClick = (item) => {
    const permissions = getPermissions();
    // Check delete permission
    if (!hasPermission(permissions.delete)) {
      toast.error(
        `ليس لديك صلاحية لحذف ${view === "package" ? "الباقات" : "القوائم"}`
      );
      return;
    }

    setSelectedItem(item);
    setOpenDelete(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (view === "package") {
      fetchPackages();
    } else {
      fetchLists();
    }
  };

  // Action columns for packages
  const packageActionColumn = [
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 40,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        const permissions = getPermissions();
        const canActivate = hasPermission(permissions.activate);

        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handlePackageActive(params.row._id)}
            disabled={!canActivate}
            style={{
              cursor: canActivate ? "pointer" : "not-allowed",
              opacity: canActivate ? 1 : 0.5,
            }}
            title={
              canActivate
                ? "تفعيل/إلغاء تفعيل الباقة"
                : "ليس لديك صلاحية لتفعيل/إلغاء تفعيل الباقات"
            }
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const permissions = getPermissions();
        const canEdit = hasPermission(permissions.edit);
        const canDelete = hasPermission(permissions.delete);

        return (
          <div className="cell-action">
            {canEdit ? (
              <Link
                className="cell-btn"
                to={`/packages/create/${params.row._id}`}
                title="تعديل الباقة"
              >
                <img src={EditUser} alt="تعديل" />
              </Link>
            ) : (
              <div
                className="cell-btn"
                style={{
                  cursor: "not-allowed",
                  opacity: 0.5,
                }}
                title="ليس لديك صلاحية لتعديل الباقات"
              >
                <img src={EditUser} alt="تعديل" />
              </div>
            )}
            <div
              className="cell-btn"
              onClick={() => handleDeleteClick(params.row)}
              style={{
                cursor: canDelete ? "pointer" : "not-allowed",
                opacity: canDelete ? 1 : 0.5,
              }}
              title={canDelete ? "حذف الباقة" : "ليس لديك صلاحية لحذف الباقات"}
            >
              <img src={DeleteUser} alt="حذف" />
            </div>
          </div>
        );
      },
    },
  ];

  // Action columns for lists
  const listActionColumn = [
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 40,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        const permissions = getPermissions();
        const canActivate = hasPermission(permissions.activate);

        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleListActive(params.row._id)}
            disabled={!canActivate}
            style={{
              cursor: canActivate ? "pointer" : "not-allowed",
              opacity: canActivate ? 1 : 0.5,
            }}
            title={
              canActivate
                ? "تفعيل/إلغاء تفعيل القائمة"
                : "ليس لديك صلاحية لتفعيل/إلغاء تفعيل القوائم"
            }
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const permissions = getPermissions();
        const canEdit = hasPermission(permissions.edit);
        const canDelete = hasPermission(permissions.delete);

        return (
          <div className="cell-action">
            <div
              className="cell-btn"
              onClick={() => handleEditList(params.row)}
              style={{
                cursor: canEdit ? "pointer" : "not-allowed",
                opacity: canEdit ? 1 : 0.5,
              }}
              title={
                canEdit ? "تعديل القائمة" : "ليس لديك صلاحية لتعديل القوائم"
              }
            >
              <img src={EditUser} alt="تعديل" />
            </div>
            <div
              className="cell-btn"
              onClick={() => handleDeleteClick(params.row)}
              style={{
                cursor: canDelete ? "pointer" : "not-allowed",
                opacity: canDelete ? 1 : 0.5,
              }}
              title={canDelete ? "حذف القائمة" : "ليس لديك صلاحية لحذف القوائم"}
            >
              <img src={DeleteUser} alt="حذف" />
            </div>
          </div>
        );
      },
    },
  ];

  // Get current data and columns based on view
  const getCurrentData = () => {
    if (view === "package") {
      // Ensure packages is always an array
      const safePackages = Array.isArray(packages) ? packages : [];
      return {
        data: safePackages.map((pkg, index) => ({
          ...pkg,
          id: pkg._id || index,
          index: (page - 1) * pageSize + index + 1,
        })),
        columns: PackagesColumns.concat(packageActionColumn),
        deleteRoute: selectedItem ? `/packages/${selectedItem._id}` : "",
        deleteMessage: "تم حذف الباقة بنجاح",
      };
    } else {
      // Ensure lists is always an array
      const safeLists = Array.isArray(lists) ? lists : [];
      return {
        data: safeLists.map((list, index) => ({
          ...list,
          id: list._id || index,
          index: (page - 1) * pageSize + index + 1,
        })),
        columns: ListsColumns.concat(listActionColumn),
        deleteRoute: selectedItem ? `/package-lists/${selectedItem._id}` : "",
        deleteMessage: "تم حذف القائمة بنجاح",
      };
    }
  };

  const currentData = getCurrentData();

  // Check if user can view current section
  const canViewPackages = hasPermission(52);
  const canViewLists = hasPermission(58);

  // If user is trying to view a section they don't have permission for
  const permissions = getPermissions();

  return (
    <div className="products">
      <div className="special-header">
        <div className="btns-container">
          <BlueButton
            text="الباقات"
            variant={view === "package" ? "fill" : "outlined"}
            size="medium"
            onClick={() => handleViewChange("package")}
            disabled={!canViewPackages}
          />
          <BlueButton
            text="القوائم"
            variant={view === "list" ? "fill" : "outlined"}
            size="medium"
            onClick={() => handleViewChange("list")}
            disabled={!canViewLists}
          />
        </div>
      </div>

      <div className="container">
        <div className="actions-container">
          {view === "package" ? (
            <div className="search">
              <TextField
                id="outlined-search"
                type="search"
                value={query}
                placeholder="ابحث باسم الباقة...."
                onChange={handleSearchChange}
                sx={{
                  width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                  borderRadius: "8px",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl
                sx={{
                  width: isMobile ? "100%" : "160px",
                }}
              >
                <InputLabel
                  htmlFor="outlined-adornment-password"
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  نوع الباقة
                </InputLabel>
                <Select
                  labelId="package-type-select"
                  id="package-type-select"
                  value={packageType}
                  onChange={handleTypeChange}
                  displayEmpty={true}
                  sx={{ borderRadius: "8px", height: "48px" }}
                >
                  <MenuItem value="">الجميع</MenuItem>
                  <MenuItem value="annually">الباقات السنوية</MenuItem>
                  <MenuItem value="monthly">الباقات الشهرية</MenuItem>
                </Select>
              </FormControl>
            </div>
          ) : (
            <div></div>
          )}

          {hasPermission(permissions.add) && (
            <BlueButton
              text={
                view === "package" ? "اضافة باقة جديدة" : "اضافة قائمة جديدة"
              }
              size={isMobile ? "full" : isTablet ? "full" : "large"}
              variant="fill"
              icon={<img src={AddGif} alt="" />}
              onClick={handleCreate}
            />
          )}
        </div>

        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={currentData.columns}
          rows={currentData.data}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage:
                view === "package" ? "باقة في الصفحة" : "قائمة في الصفحة",
            },
          }}
        />

        {/* Delete Confirmation Modal */}
        {openDelete && hasPermission(permissions.delete) && (
          <MessageModals
            open={openDelete}
            currentView="delete"
            deleteMessage={currentData.deleteMessage}
            route={currentData.deleteRoute}
            onDataChange={handleDeleteConfirm}
            onClose={() => {
              setOpenDelete(false);
              setSelectedItem(null);
            }}
          />
        )}
      </div>

      {/* SwipeableModals for Lists */}
      {isModalOpen &&
        ((currentModalView === "createList" &&
          hasPermission(permissions.add)) ||
          (currentModalView === "editList" &&
            hasPermission(permissions.edit))) && (
          <SwipeableModals
            open={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditingList(null);
            }}
            onOpen={() => setIsModalOpen(true)}
            currentView={currentModalView}
            listData={editingList}
            onSuccess={fetchLists}
          />
        )}
    </div>
  );
};

export default Packages;
